import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, Paper, Divider,
  Button, Grid, Chip, CircularProgress, Alert
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { getFirestore, doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';

// 辅助函数：从订单对象中获取金额值，同时处理多种可能的数据结构
const getAmountValue = (order, key) => {
  // 检查各种可能的金额存储结构
  const paths = [
    // 直接在order对象中存储
    order[key],
    // 在amount对象中
    order.amount && order.amount[key],
    // 在PayPal结构中的金额 (捕获详情中第一个购买单元中的金额)
    order.captureDetails?.purchase_units?.[0]?.amount?.[key],
    // 在capture amount中
    order.captureAmount?.value && key === 'total' ? order.captureAmount.value : null,
    // 在breakdown中
    order.captureDetails?.purchase_units?.[0]?.amount?.breakdown?.[key]?.value,
    // 总金额也可能直接存储在value字段
    key === 'total' && order.captureDetails?.purchase_units?.[0]?.amount?.value
  ];

  // 寻找第一个非空值
  for (const path of paths) {
    if (path !== undefined && path !== null) {
      try {
        // 确保是数字类型
        const numValue = typeof path === 'string' ? parseFloat(path) : 
                        typeof path === 'number' ? path : 
                        typeof path === 'object' ? parseFloat(path.toString()) : 0;
                        
        // 检查是否为有效数字
        if (!isNaN(numValue) && typeof numValue === 'number') {
          return numValue.toFixed(2);
        } else {
          console.warn('无法转换为数字:', path, typeof path);
        }
      } catch (err) {
        console.error('处理金额值出错:', err, '原始值:', path, typeof path);
      }
    }
  }

  // 如果找不到值，返回0
  return '0.00';
};

const OrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [products, setProducts] = useState({});

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!currentUser) {
        navigate('/login');
        return;
      }

      try {
        const db = getFirestore();
        const orderRef = doc(db, 'orders', orderId);
        const orderSnap = await getDoc(orderRef);

        if (!orderSnap.exists()) {
          setError('订单不存在');
          setLoading(false);
          return;
        }

        const orderData = {
          id: orderSnap.id,
          ...orderSnap.data(),
          createdAt: orderSnap.data().createdAt?.toDate() || new Date()
        };

        // 检查订单是否属于当前用户
        if (orderData.userId !== currentUser.uid) {
          setError('您无权查看此订单');
          setLoading(false);
          return;
        }

        // 获取订单中商品的详细信息
        const productIds = orderData.items.map(item => item.productId || item.id);
        const productsRef = collection(db, 'products');
        
        // 确保每个商品ID都存在且有效
        const validProductIds = productIds.filter(id => id && typeof id === 'string');
        
        // 获取产品详情
        let productDetails = {};
        if (validProductIds.length > 0) {
          try {
            const productDocs = await Promise.all(
              validProductIds.map(id => getDoc(doc(productsRef, id)))
            );
            
            productDocs.forEach(doc => {
              if (doc.exists()) {
                productDetails[doc.id] = doc.data();
              }
            });
            
            console.log('商品详情获取成功:', Object.keys(productDetails).length);
          } catch (productErr) {
            console.error('获取商品详情失败:', productErr);
          }
        } else {
          console.warn('订单中没有有效的商品ID');
        }

        setProducts(productDetails);
        setOrder(orderData);
      } catch (err) {
        console.error('获取订单详情失败:', err);
        setError('获取订单详情失败');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderId, currentUser, navigate]);

  const handleBackToOrders = () => {
    navigate('/user');
  };

  const handlePayment = () => {
    navigate('/checkout', { state: { orderId: order.id } });
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button 
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToOrders}
          sx={{ mt: 2 }}
        >
          返回订单列表
        </Button>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">加载订单信息...</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button 
        startIcon={<ArrowBackIcon />}
        onClick={handleBackToOrders}
        sx={{ mb: 3 }}
      >
        返回订单列表
      </Button>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">订单详情</Typography>
          <Chip 
            label={
              order.status === 'COMPLETED' || order.status === 'completed' ? '已完成' :
              order.status === 'paid' || order.status === 'PAID' ? '已付款' :
              order.status === 'CREATED' || order.status === 'created' ? '待付款' :
              order.status === 'PROCESSING' || order.status === 'processing' ? '处理中' :
              order.status === 'SHIPPED' || order.status === 'shipped' ? '已发货' :
              order.status === 'DELIVERED' || order.status === 'delivered' ? '已送达' :
              order.status === 'CANCELLED' || order.status === 'cancelled' ? '已取消' : 
              order.paymentStatus === 'completed' || order.paymentStatus === 'COMPLETED' ? '已付款' :
              '未知状态'
            }
            color={
              order.status === 'COMPLETED' || order.status === 'completed' || 
              order.status === 'paid' || order.status === 'PAID' || 
              (order.paymentStatus === 'completed' || order.paymentStatus === 'COMPLETED') ? 'success' :
              order.status === 'CREATED' || order.status === 'created' ? 'warning' :
              order.status === 'PROCESSING' || order.status === 'processing' ? 'info' :
              order.status === 'SHIPPED' || order.status === 'shipped' || 
              order.status === 'DELIVERED' || order.status === 'delivered' ? 'primary' :
              order.status === 'CANCELLED' || order.status === 'cancelled' ? 'error' : 'default'
            }
          />
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="textSecondary">订单编号</Typography>
            <Typography variant="body1">{order.id}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="textSecondary">下单时间</Typography>
            <Typography variant="body1">{order.createdAt.toLocaleString()}</Typography>
          </Grid>
          {order.paymentId && (
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="textSecondary">支付编号</Typography>
              <Typography variant="body1">{order.paymentId}</Typography>
            </Grid>
          )}
          {order.captureDetails?.purchase_units?.[0]?.payments?.captures?.[0]?.id && (
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="textSecondary">交易编号</Typography>
              <Typography variant="body1">{order.captureDetails.purchase_units[0].payments.captures[0].id}</Typography>
            </Grid>
          )}
        </Grid>

        <Typography variant="h6" gutterBottom>商品信息</Typography>
        <Divider sx={{ mb: 2 }} />

        {order.items && order.items.map((item, index) => {
          const productId = item.productId || item.id;
          const productDetails = products[productId] || {};
          
          // 确保价格是有效数字
          let price = 0;
          try {
            if (typeof item.price === 'number') {
              price = item.price;
            } else if (typeof item.price === 'string') {
              price = parseFloat(item.price);
            } else if (item.price) {
              // 尝试将其他类型转换为字符串再解析
              price = parseFloat(String(item.price));
            }
            // 确保是有效数字
            if (isNaN(price)) price = 0;
          } catch (e) {
            console.error('处理商品价格出错:', e, item);
            price = 0;
          }
          
          // 确保数量是有效整数
          let quantity = 1;
          try {
            if (typeof item.quantity === 'number') {
              quantity = item.quantity;
            } else if (typeof item.quantity === 'string') {
              quantity = parseInt(item.quantity, 10);
            } else if (item.quantity) {
              // 尝试将其他类型转换为字符串再解析
              quantity = parseInt(String(item.quantity), 10);
            }
            // 确保是有效整数且至少为1
            if (isNaN(quantity) || quantity < 1) quantity = 1;
          } catch (e) {
            console.error('处理商品数量出错:', e, item);
            quantity = 1;
          }
          
          // 获取商品图片，优先使用product详情里的图片
          const imageUrl = productDetails.imageUrl || item.imageUrl || 
                          productDetails.image || item.image || 
                          "https://storage.googleapis.com/destinycrystal-ff557.firebasestorage.app/placeholders/product-placeholder.png";
          
          return (
            <Box key={index} sx={{ py: 1, display: 'flex', alignItems: 'center' }}>
              <Box 
                component="img"
                src={imageUrl}
                alt={item.name}
                sx={{ width: 80, height: 80, objectFit: 'cover', mr: 2, borderRadius: 1 }}
                onError={(e) => {
                  // 图片加载失败时使用备用图片
                  e.target.src = "https://storage.googleapis.com/destinycrystal-ff557.firebasestorage.app/placeholders/product-placeholder.png";
                }}
              />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1">{item.name}</Typography>
                <Typography variant="body2" color="textSecondary">
                  单价: ${price.toFixed(2)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  数量: {quantity}
                </Typography>
                {productDetails.properties && (
                  <Typography variant="body2" color="textSecondary">
                    规格: {Object.entries(productDetails.properties)
                      .map(([key, value]) => `${key}: ${value}`)
                      .join(', ')}
                  </Typography>
                )}
              </Box>
              <Typography variant="subtitle1">
                ${(price * quantity).toFixed(2)}
              </Typography>
            </Box>
          );
        })}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography>商品小计:</Typography>
          <Typography>
            ${getAmountValue(order, 'subtotal')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography>运费:</Typography>
          <Typography>
            ${getAmountValue(order, 'shipping')}
          </Typography>
        </Box>

        {order.amount?.tax && parseFloat(String(order.amount.tax)) > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography>税费:</Typography>
            <Typography>
              ${getAmountValue(order, 'tax')}
            </Typography>
          </Box>
        )}

        {order.discount && parseFloat(order.discount) > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography>折扣:</Typography>
            <Typography>
              -${(typeof order.discount === 'number' ? order.discount : parseFloat(String(order.discount)) || 0).toFixed(2)}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h6">订单总计:</Typography>
          <Typography variant="h6">
            ${getAmountValue(order, 'total')}
          </Typography>
        </Box>

        {/* 收货信息显示部分 - 支持多种可能的数据结构 */}
        {(order.shipping || order.shippingAddress || order.captureDetails?.purchase_units?.[0]?.shipping) && (
          <>
            <Typography variant="h6" gutterBottom>收货信息</Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {/* 获取收货信息，支持多种数据结构 */}
              {(() => {
                // 确定使用哪个收货信息对象
                const shipping = order.shipping || 
                              order.shippingAddress || 
                              order.captureDetails?.purchase_units?.[0]?.shipping || {};
                
                // PayPal特定的数据结构
                const paypalShipping = order.captureDetails?.purchase_units?.[0]?.shipping_address || 
                                    order.captureDetails?.payer?.address;
                
                // 收货人姓名 - 可能在多个位置
                const recipientName = shipping.name || 
                                  shipping.recipient_name || 
                                  (order.captureDetails?.payer?.name && 
                                   `${order.captureDetails.payer.name.given_name || ''} ${order.captureDetails.payer.name.surname || ''}`.trim());
                
                // 电话 - 可能在多个位置
                const phone = shipping.phone || 
                          shipping.phone_number || 
                          order.captureDetails?.payer?.phone_number || 
                          '';
                
                // 地址格式化
                let formattedAddress = '';
                if (shipping.province && shipping.city) {
                  // 中国地址格式
                  formattedAddress = `${shipping.province} ${shipping.city} ${shipping.district || ''} ${shipping.address || ''}`;
                } else if (paypalShipping) {
                  // PayPal地址格式
                  const addr = paypalShipping;
                  formattedAddress = [
                    addr.address_line_1, 
                    addr.address_line_2,
                    addr.admin_area_2, // 城市
                    addr.admin_area_1, // 州/省
                    addr.postal_code,
                    addr.country_code
                  ].filter(Boolean).join(', ');
                } else if (shipping.line1) {
                  // 另一种常见格式
                  formattedAddress = [
                    shipping.line1,
                    shipping.line2,
                    shipping.city,
                    shipping.state,
                    shipping.postal_code || shipping.zip,
                    shipping.country
                  ].filter(Boolean).join(', ');
                } else if (shipping.address) {
                  // 简单地址
                  formattedAddress = shipping.address;
                }
                
                return (
                  <>
                    {recipientName && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">收货人</Typography>
                        <Typography variant="body1">{recipientName}</Typography>
                      </Grid>
                    )}
                    
                    {phone && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">联系电话</Typography>
                        <Typography variant="body1">{phone}</Typography>
                      </Grid>
                    )}
                    
                    {formattedAddress && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary">收货地址</Typography>
                        <Typography variant="body1">
                          {formattedAddress}
                        </Typography>
                      </Grid>
                    )}
                    
                    {(shipping.postalCode || shipping.postal_code || shipping.zip) && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">邮政编码</Typography>
                        <Typography variant="body1">{shipping.postalCode || shipping.postal_code || shipping.zip}</Typography>
                      </Grid>
                    )}
                    
                    {shipping.email && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">邮箱</Typography>
                        <Typography variant="body1">{shipping.email}</Typography>
                      </Grid>
                    )}
                  </>
                );
              })()}
            </Grid>
          </>
        )}

        {order.status === 'CREATED' && (
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handlePayment}
            >
              立即付款
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default OrderDetail;